import React from 'react';
import { Link } from 'react-router-dom';
import { formatCalendarDate, formatTime } from '@/lib/dates';
import { getDivisionColor } from '@/utils/divisionColors';

export interface PublicGame {
  id: string;
  status: string;
  date?: string;
  start?: string;
  venue?: string;
  court?: string;
  division?: string;
  team_a?: string;
  team_b?: string;
  team_a_id?: string;
  team_b_id?: string;
  score_a?: number | null;
  score_b?: number | null;
}

export const formatGameDay = (date?: string) => formatCalendarDate(date, 'EEE, MMM d');

function GameMeta({ game, className = '' }: { game: PublicGame; className?: string }) {
  const parts = [formatGameDay(game.date), formatTime(game.start), game.venue, game.court].filter(Boolean);
  return parts.length ? <div className={`text-xs text-muted-foreground ${className}`}>{parts.join(' · ')}</div> : null;
}

function DivisionTag({ division }: { division?: string }) {
  if (!division) return null;
  const c = getDivisionColor(division);
  return <span className="self-start rounded-full px-2.5 py-0.5 text-xs font-medium" style={{ backgroundColor: c.bg, color: c.text }}>{division}</span>;
}

interface RowProps {
  game: PublicGame;
  slug: string;
  selectedTeamId?: string;
}

export function PublicGameRow({ game: g, slug, selectedTeamId }: RowProps) {
  const c = getDivisionColor(g.division);
  const teamName = (side: 'a' | 'b') => {
    const id = side === 'a' ? g.team_a_id : g.team_b_id;
    const name = side === 'a' ? g.team_a : g.team_b;
    const content = id && id === selectedTeamId ? <strong className="font-bold">{name}</strong> : name;
    return id ? <Link to={`/schedule/${slug}/team/${id}`} className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">{content}</Link> : content;
  };

  if (g.status === 'in_progress') {
    return (
      <div data-game-id={g.id} className="rounded-xl border bg-card p-5 shadow-md" style={{ borderLeft: `5px solid ${c.border}` }}>
        <div className="mb-3 flex items-center justify-between"><GameMeta game={g} className="text-sm font-semibold" /></div>
        <div className="flex items-center justify-between gap-3">
          <span className="min-w-0 flex-1 break-words text-right text-base font-semibold text-foreground">{teamName('a')}</span>
          <span className="flex shrink-0 items-center gap-2">
            <span className="flex items-center gap-1.5 rounded-full bg-destructive px-3 py-1 text-xs font-bold uppercase tracking-wide text-destructive-foreground">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-destructive-foreground motion-reduce:animate-none" />Live
            </span>
            <span className="text-sm font-semibold text-muted-foreground">vs</span>
          </span>
          <span className="min-w-0 flex-1 break-words text-base font-semibold text-foreground">{teamName('b')}</span>
        </div>
        <div className="mt-3 flex justify-center"><DivisionTag division={g.division} /></div>
      </div>
    );
  }

  const a = g.score_a ?? 0;
  const b = g.score_b ?? 0;
  const done = g.status === 'completed';
  return (
    <div data-game-id={g.id} className="flex flex-col gap-x-4 gap-y-1 rounded-lg border bg-card px-4 py-3 shadow-sm sm:flex-row sm:flex-wrap sm:items-center" style={{ borderLeft: `4px solid ${c.border}` }}>
      <span className="min-w-0 flex-1">
        {done ? (
          <span className="flex items-center justify-between gap-3 text-sm">
            <span className={`min-w-0 break-words ${a > b ? 'font-bold text-foreground' : 'text-muted-foreground'}`}>{teamName('a')} <span className="tabular-nums">{a}</span></span>
            <span className="text-xs text-muted-foreground">–</span>
            <span className={`min-w-0 break-words ${b > a ? 'font-bold text-foreground' : 'text-muted-foreground'}`}><span className="tabular-nums">{b}</span> {teamName('b')}</span>
          </span>
        ) : <span className="block text-sm font-medium text-foreground">{teamName('a')} vs {teamName('b')}</span>}
        <GameMeta game={g} className={done ? 'mt-1' : ''} />
      </span>
      {done && a === b && <span className="shrink-0 text-xs font-semibold uppercase text-muted-foreground">Tie</span>}
      <DivisionTag division={g.division} />
    </div>
  );
}

function groupByDay(games: PublicGame[]) {
  const days = new Map<string, PublicGame[]>();
  games.forEach((game) => {
    const date = game.date || '';
    const list = days.get(date) || [];
    list.push(game);
    days.set(date, list);
  });
  return [...days.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, list]) => ({
    date,
    games: list.sort((a, b) => (a.start || '').localeCompare(b.start || '') || (a.court || '').localeCompare(b.court || '', undefined, { numeric: true })),
  }));
}

export function PublicGameSections({ games, slug, selectedTeamId, upcomingLimit }: {
  games: PublicGame[];
  slug: string;
  selectedTeamId?: string;
  upcomingLimit?: number;
}) {
  const sorted = [...games].sort((a, b) => `${a.date || ''} ${a.start || ''}`.localeCompare(`${b.date || ''} ${b.start || ''}`));
  const upcoming = sorted.filter((g) => g.status === 'scheduled');
  const sections = [
    { title: 'Now Playing', games: sorted.filter((g) => g.status === 'in_progress'), empty: 'No games in progress.', live: true },
    { title: 'Up Next', games: upcomingLimit === undefined ? upcoming : upcoming.slice(0, upcomingLimit), empty: 'No upcoming games.', live: false },
    { title: 'Final Scores', games: sorted.filter((g) => g.status === 'completed'), empty: 'No completed games yet.', live: false },
  ];
  return <>{sections.map((section) => (
    <section className="mb-10" key={section.title}>
      <h2 className={`mb-4 text-xl font-semibold text-primary ${section.live ? 'flex items-center gap-2' : ''}`}>
        {section.live && <span className="relative flex h-3 w-3"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75 motion-reduce:animate-none" /><span className="relative inline-flex h-3 w-3 rounded-full bg-destructive" /></span>}
        {section.title}
      </h2>
      {section.games.length === 0 ? <p className="text-sm text-muted-foreground">{section.empty}</p> : section.live ? (
        <div className="grid gap-4 sm:grid-cols-2">{section.games.map((game) => <PublicGameRow key={game.id} game={game} slug={slug} selectedTeamId={selectedTeamId} />)}</div>
      ) : (
        <div className="flex flex-col gap-2">{groupByDay(section.games).map(({ date, games: dayGames }) => (
          <React.Fragment key={date}>
            <h3 className="mb-2 mt-4 text-base font-semibold text-primary first:mt-0">{formatCalendarDate(date, 'EEEE, MMMM d')}</h3>
            {dayGames.map((game) => <PublicGameRow key={game.id} game={game} slug={slug} selectedTeamId={selectedTeamId} />)}
          </React.Fragment>
        ))}</div>
      )}
    </section>
  ))}</>;
}